import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';

const jsonLd = {
  '@type': 'WebPage',
  name: 'Mulebuy Spreadsheet Shopping Guide',
  description: 'Your complete guide to smart buying with mulebuy spreadsheet. Learn how to navigate categories, compare agents, and make confident purchasing decisions across all product categories.',
  mainEntityOfPage: '/shopping-guide',
};

export default function ShoppingGuide() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <SEO
        title="Mulebuy Spreadsheet Shopping Guide | Navigate Categories &amp; Buy Smart"
        description="Your complete guide to smart buying with mulebuy spreadsheet. Learn how to navigate shoes, hoodies, t-shirts, jackets, bags, accessories and more categories on the main platform."
        keywords="mulebuy shopping guide, spreadsheet shopping guide, mulebuy categories, spreadsheet buying, mulebuy directory"
        canonicalPath="/shopping-guide"
        jsonLd={jsonLd}
      />
      <Header />
      <main className="pt-20 pb-16 bg-background-50">
        <section className="max-w-4xl mx-auto px-6 md:px-10">
          <nav className="text-xs text-foreground-500 mb-6">
            <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground-800">Shopping Guide</span>
          </nav>

          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 leading-tight mb-4">
            Mulebuy Spreadsheet Shopping Guide
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mb-10 leading-relaxed max-w-2xl">
            Your complete guide to smart buying with mulebuy spreadsheet. Learn how to navigate categories, compare agents, and make confident purchasing decisions.
          </p>

          <div className="w-full h-56 md:h-80 rounded-2xl overflow-hidden mb-10">
            <img
              src="https://readdy.ai/api/search-image?query=Woman%20shopping%20online%20with%20laptop%20surrounded%20by%20fashion%20items%20and%20shopping%20bags%2C%20bright%20airy%20living%20room%2C%20modern%20ecommerce%20lifestyle%2C%20happy%20shopping%20experience%2C%20warm%20natural%20lighting&width=1200&height=600&seq=shopping-guide-hero&orientation=landscape"
              alt="Mulebuy spreadsheet shopping guide"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="prose-custom max-w-none space-y-10">
            <div>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">How to Use This Shopping Guide</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                This <strong>mulebuy spreadsheet</strong> shopping guide is designed to help you navigate every step of the buying process — from browsing product categories to finalizing your order. Use the category links below to jump directly to the products you need on the main platform.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6">Product Categories</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { name: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html', icon: 'ri-footprint-line' },
                  { name: 'Hoodies/Sweaters', url: 'https://kaspreadsheet.com/category-912-Hoodies%2FSweaters.html', icon: 'ri-t-shirt-line' },
                  { name: 'T-Shirts', url: 'https://kaspreadsheet.com/category-913-T-Shirts.html', icon: 'ri-t-shirt-2-line' },
                  { name: 'Jackets', url: 'https://kaspreadsheet.com/category-914-Jackets.html', icon: 'ri-shirt-line' },
                  { name: 'Pants/Shorts', url: 'https://kaspreadsheet.com/category-915-Pants%2FShorts.html', icon: 'ri-pantone-line' },
                  { name: 'Headwear', url: 'https://kaspreadsheet.com/category-916-Headwear.html', icon: 'ri-user-star-line' },
                  { name: 'Sets', url: 'https://kaspreadsheet.com/category-917-Sets.html', icon: 'ri-stack-line' },
                  { name: 'Underwear', url: 'https://kaspreadsheet.com/category-918-underwear%2Funderpants.html', icon: 'ri-check-line' },
                  { name: 'Jersey', url: 'https://kaspreadsheet.com/category-919-Jersey.html', icon: 'ri-basketball-line' },
                  { name: 'Accessories', url: 'https://kaspreadsheet.com/category-920-Accessories.html', icon: 'ri-vip-diamond-line' },
                  { name: 'Bags', url: 'https://kaspreadsheet.com/category-921-Bags.html', icon: 'ri-handbag-line' },
                  { name: 'Belts', url: 'https://kaspreadsheet.com/search-922/name/Bags.html', icon: 'ri-links-line' },
                  { name: 'Perfumes', url: 'https://kaspreadsheet.com/search-923/name/Bags.html', icon: 'ri-drop-line' },
                  { name: 'Other Stuff', url: 'https://kaspreadsheet.com/search-924/name/Bags.html', icon: 'ri-more-line' },
                ].map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-white border border-background-200/70 cursor-pointer hover:border-primary-300 hover:bg-background-50 transition-all whitespace-nowrap"
                  >
                    <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                      <i className={`${cat.icon} text-foreground-600 text-sm`}></i>
                    </div>
                    <span className="text-sm text-foreground-800 font-medium">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">Shopping Tips</h2>
              <div className="space-y-3">
                {[
                  { title: 'Compare Before You Buy', desc: 'Always check at least three agents in the mulebuy spreadsheet before making a purchase decision. Small price differences add up quickly on bulk orders.' },
                  { title: 'Check Update Dates', desc: 'The mulebuy spreadsheet is updated weekly. Always verify the last-updated timestamp to ensure you are working with fresh data.' },
                  { title: 'Start Small', desc: 'When trying a new agent from the mulebuy spreadsheet, place a small test order first. This lets you verify quality and service before committing to larger purchases.' },
                  { title: 'Read Our Guides', desc: 'Browse our SEO articles for detailed comparisons, tutorials, and safety tips that will make you a smarter buyer.' },
                ].map((tip, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 flex gap-3">
                    <span className="text-accent-500 font-bold text-sm flex-shrink-0">0{i + 1}</span>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-1">{tip.title}</h3>
                      <p className="text-xs text-foreground-600 leading-relaxed">{tip.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">Need More Help?</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Check out our detailed <strong>mulebuy spreadsheet</strong> guides covering everything from beginner basics to advanced comparison strategies. Each article includes practical tips, comparison tables, and direct links to get you started.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/what-is-mulebuy-spreadsheet" className="px-5 py-2.5 rounded-full bg-background-100 text-sm text-foreground-800 font-medium cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap">What Is Mulebuy Spreadsheet</Link>
                <Link to="/best-mulebuy-spreadsheets" className="px-5 py-2.5 rounded-full bg-background-100 text-sm text-foreground-800 font-medium cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap">Best Lists 2026</Link>
                <Link to="/is-mulebuy-spreadsheet-safe" className="px-5 py-2.5 rounded-full bg-background-100 text-sm text-foreground-800 font-medium cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap">Safety Guide</Link>
              </div>
            </div>
          </div>

          <div className="mt-12 p-6 md:p-8 bg-accent-100/60 rounded-2xl text-center">
            <p className="font-heading text-lg text-foreground-900 font-semibold">Ready to shop? Go to the main platform now</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-5">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-6 py-2.5 rounded-full bg-foreground-950 text-white text-sm font-semibold cursor-pointer hover:bg-foreground-800 transition-colors whitespace-nowrap">Go to Main Platform</a>
              <Link to="/" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">&larr; Back to Home</Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}